import { inject, Injectable } from '@angular/core';
import { SmsContextToken } from '../token';
import { catchError, combineLatest, distinctUntilChanged, map, Observable, of, switchMap } from 'rxjs';
import {
  ConfigApiService,
  ConversationApiService,
  OwnerType,
  PhoneNumberConfig,
  PhoneNumberConfigInterface,
} from '@vendasta/smsv2';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { GetPhoneNumberConfigResponse } from '@vendasta/smsv2/lib/_internal/objects';
import { GlobalParticipantType, InboxApiService } from '@vendasta/conversation';

@Injectable({
  providedIn: 'root',
})
export class SmsConfigService {
  private readonly context = inject(SmsContextToken);
  private readonly configApiService = inject(ConfigApiService);
  private readonly conversationAPI = inject(ConversationApiService);
  private readonly inboxAPI = inject(InboxApiService);
  public readonly phoneNumberConfig$: Observable<PhoneNumberConfig>;
  public readonly usesBroadlyTwilio$ = this.context.sendsViaBroadly$;
  public readonly excludedFromVoiceAI$: Observable<boolean>;
  // BREW-1431: NBLY Voice Receptionist POC
  private readonly voiceAINBLYAccountGroups = [
    'AG-6R772G8ZJ6',
    'AG-R6VN65LK8H',
    'AG-JQB5ZTP4X8',
    'AG-2BPQCT27GH',
    'AG-4T2TR48LM2',
    'AG-N42V33GCVZ',
    'AG-G4PHF8MR2F',
    'AG-8PFGWWLM6Z',
    'AG-DKL4G3STWT',
    'AG-H4V5C6NWX3',
    'AG-K5BM4K87TC',
    'AG-4TW6XV2NGS',
    'AG-8B7PMHHMXB',
    'AG-G3X6QF3RQK',
    'AG-S8FGCZHRHK',
    'AG-KT6B555PK6',
    'AG-GLH3JJKCDB',
    'AG-RG2CHS8BZJ',
    'AG-RHXHZW4FJX',
    'AG-TLS2PV3CMR',
  ];

  constructor() {
    this.phoneNumberConfig$ = this.context.ownerId$.pipe(
      switchMap((ownerId) =>
        this.configApiService.getPhoneNumberConfig({ ownerId: ownerId, ownerType: this.context.ownerType }).pipe(
          catchError(() => {
            return of({ phoneNumberConfig: {} } as GetPhoneNumberConfigResponse);
          }),
        ),
      ),
      map((response) => response.phoneNumberConfig),
    );

    // TODO(BREW-1642): Remove this once we cleanup the NBLY pilot list: voiceAINBLYAccountGroups
    this.excludedFromVoiceAI$ = combineLatest([this.context.featureFlagInfo$, this.context.ownerId$]).pipe(
      distinctUntilChanged(),
      switchMap(([featureFlagInfo, ownerId]) => {
        if (featureFlagInfo.partnerId !== 'NBLY') {
          return of(false);
        }
        if (this.voiceAINBLYAccountGroups.includes(ownerId)) {
          return of(false);
        }
        const participantType =
          this.context.ownerType === OwnerType.OWNER_TYPE_ACCOUNT_GROUP
            ? GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP
            : GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER;
        return this.inboxAPI
          .getConfiguration({
            subjectParticipant: {
              participantType: participantType,
              internalParticipantId: ownerId,
            },
          })
          .pipe(
            map((response) => !response.configuration.aiVoiceEnabled),
            catchError((error) => {
              if (error instanceof HttpErrorResponse && error.status === 404) {
                return of(true);
              }
              return of(false);
            }),
          );
      }),
    );
  }

  public upsertPhoneNumberConfig(config: PhoneNumberConfigInterface): Observable<HttpResponse<null>> {
    return this.context.ownerId$.pipe(
      switchMap((ownerId) => {
        const payload = {
          ownerId: ownerId,
          ownerType: this.context.ownerType,
          phoneNumberConfig: config,
        };

        return this.configApiService.upsertPhoneNumberConfig(payload);
      }),
    );
  }

  public claimPhoneNumber(): Observable<HttpResponse<null>> {
    return this.context.ownerId$.pipe(
      switchMap((ownerId) => {
        const payload = {
          ownerId: ownerId,
          ownerType: this.context.ownerType,
        };

        return this.conversationAPI.claimPhoneNumber(payload);
      }),
    );
  }
}
